/**
 * Deposit allocation math. Implements REQ-3.4.
 *
 * Given a deposit amount (integer minor units) and a set of buckets
 * whose `allocationBps` sum to exactly 10,000, produce per-bucket
 * amounts that sum back to the original deposit amount with banker's
 * rounding and a deterministic tie-break for the remainder.
 */

import { BPS_TOTAL, assertSafeMinorUnits } from './money';

export interface AllocationInput {
  bucketId: number;
  allocationBps: number;
  sortOrder: number;
}

export interface AllocationResult {
  bucketId: number;
  amountMinorUnits: number;
  allocationBpsSnapshot: number;
}

export function allocateDeposit(
  depositMinorUnits: number,
  buckets: ReadonlyArray<AllocationInput>,
): AllocationResult[] {
  assertSafeMinorUnits(depositMinorUnits);
  if (depositMinorUnits <= 0) {
    throw new Error('deposit amount must be positive');
  }
  if (buckets.length === 0) {
    throw new Error('at least one bucket is required');
  }
  const bpsSum = buckets.reduce((acc, b) => acc + b.allocationBps, 0);
  if (bpsSum !== BPS_TOTAL) {
    throw new Error(`bucket allocation_bps must sum to ${BPS_TOTAL}, got ${bpsSum}`);
  }

  // Floor per bucket, then distribute the remainder.
  const floored = buckets.map((b) => ({
    bucketId: b.bucketId,
    allocationBpsSnapshot: b.allocationBps,
    // Integer math only — no floats anywhere in money paths.
    amountMinorUnits: Math.floor((depositMinorUnits * b.allocationBps) / BPS_TOTAL),
    // Remainder ranking key — higher allocationBps wins; ties broken by lower sortOrder.
    tieBreaker: b.sortOrder,
  }));

  const distributed = floored.reduce((acc, r) => acc + r.amountMinorUnits, 0);
  let remainder = depositMinorUnits - distributed;

  /* c8 ignore start -- defensive: floor sums cannot exceed the deposit */
  if (remainder < 0) {
    throw new Error('internal allocation error: negative remainder');
  }
  /* c8 ignore stop */

  // Hand remainder minor units to buckets in order of highest allocation_bps,
  // tie-break by lowest sort_order. Deterministic.
  const priority = [...floored].sort((a, b) => {
    if (b.allocationBpsSnapshot !== a.allocationBpsSnapshot) {
      return b.allocationBpsSnapshot - a.allocationBpsSnapshot;
    }
    return a.tieBreaker - b.tieBreaker;
  });

  let index = 0;
  while (remainder > 0) {
    const target = priority[index % priority.length];
    /* c8 ignore start -- defensive: priority is non-empty when remainder > 0 */
    if (!target) {
      break;
    }
    /* c8 ignore stop */
    target.amountMinorUnits += 1;
    remainder -= 1;
    index += 1;
  }

  return floored.map(({ bucketId, amountMinorUnits, allocationBpsSnapshot }) => ({
    bucketId,
    amountMinorUnits,
    allocationBpsSnapshot,
  }));
}

/**
 * Validate that a set of bucket allocations satisfies REQ-3.1: every
 * allocationBps is in [0, 10000] and the sum is exactly 10000.
 *
 * Returns `null` on success; on failure returns a short human-readable
 * error message suitable for surfacing to the user or logging.
 *
 * Used by US-BUCKET-01 when creating the default buckets and by future
 * US-BUCKET-03 when a guardian edits the split.
 */
export function validateBucketAllocations(allocations: ReadonlyArray<number>): string | null {
  if (allocations.length === 0) {
    return 'at least one bucket is required';
  }
  for (const bps of allocations) {
    if (!Number.isInteger(bps)) {
      return `allocation must be an integer (got ${bps})`;
    }
    if (bps < 0 || bps > BPS_TOTAL) {
      return `allocation must be between 0 and ${BPS_TOTAL} bps (got ${bps})`;
    }
  }
  const sum = allocations.reduce((a, b) => a + b, 0);
  if (sum !== BPS_TOTAL) {
    return `allocations must sum to exactly ${BPS_TOTAL} bps (got ${sum})`;
  }
  return null;
}

/**
 * Default bucket template for a newly-created child (REQ-3.2). Three
 * buckets summing to 10,000 bps. Guardian can edit these later
 * (US-BUCKET-03).
 */
export const DEFAULT_CHILD_BUCKETS: ReadonlyArray<{
  name: string;
  allocationBps: number;
  isGuardianManaged: boolean;
  requiresApprovalToWithdraw: boolean;
  displayColor: string;
  sortOrder: number;
}> = [
  {
    name: 'Spend',
    allocationBps: 3500,
    isGuardianManaged: false,
    requiresApprovalToWithdraw: false,
    displayColor: '#f07a2e',
    sortOrder: 0,
  },
  {
    name: 'Save',
    allocationBps: 5000,
    isGuardianManaged: true,
    requiresApprovalToWithdraw: true,
    displayColor: '#2d6a9f',
    sortOrder: 1,
  },
  {
    name: 'Tithe',
    allocationBps: 1500,
    isGuardianManaged: false,
    requiresApprovalToWithdraw: false,
    displayColor: '#4a9b5f',
    sortOrder: 2,
  },
];
